import type { EventEmitter } from '@stencil/core';
import { Component, Element, Event, Host, Method, Prop, State, Watch, h } from '@stencil/core';
import { getParentElement } from '../../utils/select-elements.utils';

/**
 * @slot - Slot to add components such as sy-text and sy-progress-spinner.
 */
@Component({
  tag: 'sy-infinite-scroll',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class InfiniteScroll {
  // Threshold Pixels
  private thrPx = 0;

  // Threshold Percentage
  private thrPc = 0;

  // The Scroll Element
  private scrollEl?: HTMLElement;

  // If Event did fire
  private didFire = false;

  @Element() el!: HTMLSyInfiniteScrollElement;
  @State() isLoading = false;

  /**
   * Optional. A reference to a specific element that should be scrolled.
   * If none is provided, the component will set the elementToBeScrolled to be it's parent.
   */
  @Prop() elementToBeScrolled?: HTMLElement;

  /**
   * The threshold distance from the bottom of the content to call the `infinite` output event when scrolled.
   * This value can be specified as the scroll's remaining pixels (e.g., 200px) or a percentage of the total scroll size (e.g., 15%).
   */
  @Prop() threshold = '15%';

  @Watch('threshold')
  protected tresholdWatcher(): void {
    const val = this.threshold;
    if (val.lastIndexOf('%') > -1) {
      this.thrPx = 0;
      this.thrPc = parseFloat(val) / 100;
    } else {
      this.thrPx = parseFloat(val);
      this.thrPc = 0;
    }
  }

  /**
   * If the infinite scroll should be hidden and disabled.
   */
  @Prop() disabled = false;

  @Watch('disabled')
  protected disabledWatcher(): void {
    const disabled = this.disabled;
    if (disabled) {
      this.isLoading = false;
    }
    this.enableScrollEvents(!disabled);
  }

  /**
   * Emitted when the scroll reaches the threshold distance.
   * You must call the component's `complete()` method when your async operation has completed.
   */
  @Event() syScrollDidReachThreshold: EventEmitter<void>;

  /**
   * Emitted when the scroll reaches the top.
   */
  @Event() syScrollDidReachTop: EventEmitter<void>;

  async connectedCallback() {
    if (this.elementToBeScrolled) {
      this.scrollEl = this.elementToBeScrolled;
    } else {
      const contentEl = getParentElement(this.el);
      this.scrollEl = contentEl.el as any;
    }
    this.tresholdWatcher();
    this.disabledWatcher();
  }

  disconnectedCallback() {
    this.enableScrollEvents(false);
    this.scrollEl = undefined;
  }

  /**
   * Important: Call `complete()` within the `syInfinite` output event handler when your async operation has completed.
   */
  @Method()
  async complete(): Promise<void> {
    const scrollEl = this.scrollEl;
    if (!this.isLoading || !scrollEl) {
      return;
    }
    this.isLoading = false;
  }

  private onScroll = () => {
    const scrollEl = this.scrollEl;
    if (!scrollEl || !this.canStart()) {
      return;
    }

    const infiniteHeight = this.el.offsetHeight;
    const scrollTop = scrollEl.scrollTop;
    const scrollHeight = scrollEl.scrollHeight;
    const height = scrollEl.offsetHeight;
    const threshold = this.thrPc === 0 ? this.thrPx : height * this.thrPc;

    const distanceFromInfinite = scrollHeight - infiniteHeight - scrollTop - threshold - height;

    if (distanceFromInfinite < 0) {
      if (!this.didFire) {
        this.isLoading = true;
        this.didFire = true;
        this.syScrollDidReachThreshold.emit();
        return;
      }
    } else if (scrollTop === 0) {
      if (!this.didFire) {
        this.didFire = true;
        this.syScrollDidReachTop.emit();
        return;
      }
    } else {
      this.didFire = false;
    }

    return;
  };

  private canStart(): boolean {
    return !this.disabled && !!this.scrollEl && !this.isLoading;
  }

  private enableScrollEvents(shouldListen: boolean) {
    if (this.scrollEl) {
      if (shouldListen) {
        this.scrollEl.addEventListener('scroll', this.onScroll);
      } else {
        this.scrollEl.removeEventListener('scroll', this.onScroll);
      }
    }
  }

  render() {
    const disabled = this.disabled;
    return (
      <Host
        class={{
          'infinite-scroll-loading': this.isLoading,
          'infinite-scroll-enabled': !disabled,
        }}
      >
        <slot />
      </Host>
    );
  }
}
